import React, { useCallback, useMemo, useState } from 'react';
import { useAppDispatch } from '../../store';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import Button from '../shared/TailwindComponents/Button';
import Badge from '../shared/TailwindComponents/Badge';
import { BadgeColor } from '../shared/TailwindComponents/types/badgeTypes';
import FilterBarWithTabs from '../shared/TailwindComponents/FilterBarWithTabs';
import {
  PiPlusLight,
  PiTrashLight,
  PiPencilSimpleLight,
  PiCaretDownLight,
  PiCheckLight,
  PiProhibitLight,
  PiInfoLight,
  PiArrowRightLight,
  PiArrowBendDownRightLight,
  PiTagLight,
  PiNoteLight,
} from 'react-icons/pi';
import { hideModal, showModal } from '../../store/actions/modalActions';
import { showSidePanel } from '../../store/actions/sidePanelActions';
import {
  deleteClauseRule,
} from '../../store/actions/Review/ReviewActions';
import { useFeatureTour } from '../../hooks/useFeatureTour';
import { FeatureTour, clauseRulesTourSteps } from '../shared/FeatureTour';
import GlassCard from '../shared/brand/GlassCard';
import { Well } from '../shared/brand/Field';
import { ClauseRule } from '../../types/review';

/** Convert snake_case to Title Case: 'service_levels' → 'Service Levels' */
const formatClauseType = (text: string): string =>
  text
    .replace(/_/g, ' ')
    .replace(/(^|\s)\S/g, (c) => c.toUpperCase());

/**
 * Risk (1–10) is one scale, so it gets one tokenized band rather than a flat
 * red badge: low reads as success, mid as warning, high as danger. The same
 * band drives the card's left accent, so a list scans by colour before it
 * scans by text.
 */
type RiskBand = 'low' | 'medium' | 'high';

const riskBandOf = (riskWeight: number): RiskBand =>
  riskWeight >= 8 ? 'high' : riskWeight >= 4 ? 'medium' : 'low';

const RISK_BAND: Record<RiskBand, { accent: string; badge: BadgeColor }> = {
  low: { accent: 'border-l-success-400 dark:border-l-success-600', badge: 'green' },
  medium: { accent: 'border-l-warning-400 dark:border-l-warning-600', badge: 'yellow' },
  high: { accent: 'border-l-danger-400 dark:border-l-danger-600', badge: 'red' },
};

/**
 * The one section heading used by every block of an expanded rule — a small
 * muted icon + label, so "what it checks", "we accept" and "we won't accept"
 * all read at the same altitude and only their well is tinted.
 */
const SectionLabel: React.FC<{ icon: React.ReactNode; label: string }> = ({ icon, label }) => (
  <div className="flex items-center gap-1.5 mb-1.5">
    <span className="text-fg-muted [&>svg]:w-3.5 [&>svg]:h-3.5 shrink-0">{icon}</span>
    <span className="text-[11px] font-medium uppercase tracking-wide text-fg-muted">{label}</span>
  </div>
);

interface ReviewClauseRulesProps {
  reviewUuid: string;
  clauseRules: ClauseRule[];
  readOnly?: boolean;
}

const ReviewClauseRules: React.FC<ReviewClauseRulesProps> = ({ reviewUuid, clauseRules, readOnly }) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const [expandedRuleId, setExpandedRuleId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [riskFilter, setRiskFilter] = useState('all');

  // Tour
  const { isRunning: isTourRunning, handleJoyrideCallback: handleTourCallback } = useFeatureTour({
    tourId: 'clause-rules',
    steps: clauseRulesTourSteps,
    readyToShow: true,
    autoStartDelay: 600,
  });

  // ─── Filter + default-sort logic (by risk weight descending) ────────
  const filteredRules = useMemo(() => {
    let rules = [...clauseRules].sort((a, b) => (b.risk_weight || 0) - (a.risk_weight || 0));

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      rules = rules.filter((r) =>
        (r.display_name || '').toLowerCase().includes(q) ||
        r.clause_type.toLowerCase().includes(q) ||
        (r.description || '').toLowerCase().includes(q) ||
        (r.tags || []).some((tag) => tag.toLowerCase().includes(q))
      );
    }

    if (riskFilter !== 'all') {
      rules = rules.filter((r) => {
        const rw = r.risk_weight || 1;
        if (riskFilter === 'high') return rw >= 8;
        if (riskFilter === 'medium') return rw >= 4 && rw <= 7;
        if (riskFilter === 'low') return rw <= 3;
        return true;
      });
    }

    return rules;
  }, [clauseRules, searchQuery, riskFilter]);

  // ─── Side panel handlers ─────────────────────────────────────────────

  const handleAddRule = useCallback(() => {
    dispatch(showSidePanel({
      type: 'editClauseRule',
      showSidePanel: true,
      size: 'md',
      message: t('New Clause Rule'),
      reviewUuid,
      actions: [
        { label: t('Save Rule'), variant: 'primary' as const, onClick: (ref: any) => ref?.save?.() },
      ],
    }));
  }, [dispatch, reviewUuid, t]);

  const handleEdit = useCallback((rule: ClauseRule) => {
    dispatch(showSidePanel({
      type: 'editClauseRule',
      showSidePanel: true,
      size: 'md',
      message: t('Edit Clause Rule'),
      reviewUuid,
      rule,
      actions: [
        { label: t('Update Rule'), variant: 'primary' as const, onClick: (ref: any) => ref?.save?.() },
      ],
    }));
  }, [dispatch, reviewUuid, t]);

  const handleDelete = useCallback((ruleUuid: string) => {
    dispatch(
      showModal({
        type: 'confirmation',
        title: t('Delete Clause Rule'),
        message: t('This will permanently remove this clause rule from the playbook. This action cannot be undone.'),
        icon: <PiTrashLight className='w-6 h-6 text-danger-600' />,
        variant: 'danger',
        isDest: true,
        showModal: true,
        onConfirm: async () => {
          await dispatch(deleteClauseRule(reviewUuid, ruleUuid) as any);
          dispatch(hideModal());
        },
        onCancel: () => dispatch(hideModal()),
      }),
    );
  }, [dispatch, reviewUuid, t]);

  const toggleExpand = useCallback((ruleUuid: string) => {
    setExpandedRuleId((prev) => prev === ruleUuid ? null : ruleUuid);
  }, []);

  return (
    <div className="pb-10 space-y-0">
      {/* Filter bar with search, risk filter, and add button */}
      <div data-tour-id="clause-rules-header" className="pt-4 px-2">
        <FilterBarWithTabs
          onSearchChange={setSearchQuery}
          filters={[
            {
              id: 'risk',
              name: t('Risk Level'),
              options: [
                { value: 'high', label: t('High (8-10)') },
                { value: 'medium', label: t('Medium (4-7)') },
                { value: 'low', label: t('Low (1-3)') },
              ],
            },
          ]}
          selectedFilters={{ risk: riskFilter !== 'all' ? [riskFilter] : [] }}
          onFilterChange={(filterName, values) => setRiskFilter(values.length > 0 ? values[0] : 'all')}
          actions={!readOnly ? [
            {
              label: t('Add Rule'),
              onClick: handleAddRule,
              icon: <PiPlusLight className="w-4 h-4" />,
            },
          ] : []}
          noParentPadding
          noBorder
        />
      </div>

      <div className="px-4 pt-4 space-y-2">

      {/* Inline form — kept commented out for reference after side panel migration */}
      {/* {showForm && !readOnly && (
        <div className="p-4 bg-surface-sunken rounded-lg border border-hairline space-y-3">
          ... inline form was here ...
        </div>
      )} */}

      {/* Rules list */}
      {filteredRules.length === 0 && clauseRules.length === 0 ? (
        <div className="text-center py-12 border-2 border-dashed border-hairline rounded-lg">
          <p className="text-sm text-fg-muted">
            {readOnly
              ? t('No clause rules defined for this playbook.')
              : t('No clause rules yet. Add rules to define what clauses the AI should check.')}
          </p>
          {!readOnly && (
            <Button
              variant="neutral"
              size="small"
              className="mt-3"
              onClick={handleAddRule}
            >
              <PiPlusLight className="w-4 h-4 mr-1" />
              {t('Add First Rule')}
            </Button>
          )}
        </div>
      ) : filteredRules.length === 0 ? (
        <div className="text-center py-8">
          <p className="text-sm text-fg-muted">
            {t('No rules match your search.')}
          </p>
        </div>
      ) : (
        <div className="space-y-2" data-tour-id="clause-rules-list">
          {filteredRules.map((rule) => {
            const rw = rule.risk_weight || 1;
            const band = riskBandOf(rw);
            const riskBadgeColor: BadgeColor = RISK_BAND[band].badge;
            const riskLabel = band === 'high' ? t('High risk') : band === 'medium' ? t('Medium risk') : t('Low risk');
            const isExpanded = expandedRuleId === rule.uuid;

            return (
              <GlassCard
                key={rule.uuid}
                padding="none"
                className={clsx(
                  'overflow-hidden border-l-[3px] hover:shadow-md transition-all duration-200 ease-slide',
                  RISK_BAND[band].accent,
                )}
              >
                {/*
                  Header row — a click expands the card in place. EDITING is
                  the pencil, which opens the full `editClauseRule` side panel
                  (see `handleEdit`); the caret is its own button so the
                  summary can also be toggled from the chevron.
                */}
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => toggleExpand(rule.uuid)}
                  onKeyDown={(e) => {
                    if (e.key !== 'Enter' && e.key !== ' ') return;
                    e.preventDefault();
                    toggleExpand(rule.uuid);
                  }}
                  className="w-full text-left px-4 py-3 cursor-pointer"
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <span className="text-sm font-medium text-fg-strong truncate">
                        {rule.display_name || formatClauseType(rule.clause_type)}
                      </span>
                      <Badge color="blue">{formatClauseType(rule.clause_type)}</Badge>
                      <Badge color={riskBadgeColor} tooltip={t('Risk weight (1-10): {{label}}', { label: riskLabel })}>
                        {t('Risk')} {rw}/10
                      </Badge>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      {!readOnly && (
                        <>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); handleEdit(rule); }}
                            className="p-1 rounded-md text-fg-muted hover:text-champagne hover:bg-surface-sunken/70 transition-colors"
                            title={t('Edit')}
                          >
                            <PiPencilSimpleLight className="w-3.5 h-3.5" />
                          </button>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); handleDelete(rule.uuid); }}
                            className="p-1 rounded-md text-fg-muted hover:text-danger-500 hover:bg-surface-sunken/70 transition-colors"
                            title={t('Delete')}
                          >
                            <PiTrashLight className="w-3.5 h-3.5" />
                          </button>
                        </>
                      )}
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); toggleExpand(rule.uuid); }}
                        className="p-1 rounded-md text-fg-muted hover:text-fg-body hover:bg-surface-sunken/70 transition-colors"
                        title={isExpanded ? t('Collapse') : t('Expand')}
                        aria-expanded={isExpanded}
                      >
                        <PiCaretDownLight
                          className={clsx(
                            'w-3.5 h-3.5 transition-transform duration-200',
                            isExpanded && 'rotate-180',
                          )}
                        />
                      </button>
                    </div>
                  </div>
                  {rule.description && !isExpanded && (
                    <p className="text-xs text-fg-muted mt-1 line-clamp-1 leading-relaxed max-w-3xl">
                      {rule.description}
                    </p>
                  )}
                </div>

                {/*
                  Expanded content — one label/well rhythm for every block.
                  Neutral sections are plain sunken wells; the positional ones
                  keep their semantic colour, but as a tinted well with a left
                  accent rather than a box of their own.
                */}
                {isExpanded && (
                  <div className="border-t border-hairline-soft px-4 py-4 space-y-4">
                    {/* What is this rule checking? */}
                    {rule.description && (
                      <div className="max-w-2xl">
                        <SectionLabel icon={<PiInfoLight />} label={t('What this rule checks')} />
                        <Well>
                          <p className="text-sm text-fg-body leading-relaxed">{rule.description}</p>
                        </Well>
                      </div>
                    )}

                    {/* Our preferred position */}
                    {rule.starting_position && (
                      <div className="max-w-2xl">
                        <SectionLabel icon={<PiArrowRightLight />} label={t('Our preferred position')} />
                        <Well>
                          <p className="text-sm text-fg-body leading-relaxed">{rule.starting_position}</p>
                        </Well>
                      </div>
                    )}

                    {/* Acceptable variations — success-toned well */}
                    {rule.acceptable_variations?.length > 0 && (
                      <div className="max-w-2xl">
                        <SectionLabel icon={<PiCheckLight />} label={t('We accept')} />
                        <Well className="space-y-1 border-l-2 border-l-success-400 dark:border-l-success-600 bg-success-50/70 dark:bg-success-900/20">
                          {rule.acceptable_variations.map((v, i) => (
                            <p key={i} className="text-sm text-success-900 dark:text-success-100 leading-relaxed">{v}</p>
                          ))}
                        </Well>
                      </div>
                    )}

                    {/* Red lines — danger-toned well */}
                    {rule.red_lines?.length > 0 && (
                      <div className="max-w-2xl">
                        <SectionLabel icon={<PiProhibitLight />} label={t("We won't accept")} />
                        <Well className="space-y-1 border-l-2 border-l-danger-400 dark:border-l-danger-600 bg-danger-50/70 dark:bg-danger-900/20">
                          {rule.red_lines.map((rl, i) => (
                            <p key={i} className="text-sm text-danger-900 dark:text-danger-100 leading-relaxed">{rl}</p>
                          ))}
                        </Well>
                      </div>
                    )}

                    {/* Fallback position */}
                    {rule.fallback_position && (
                      <div className="max-w-2xl">
                        <SectionLabel icon={<PiArrowBendDownRightLight />} label={t('If we must compromise')} />
                        <Well className="border-l-2 border-l-warning-400 dark:border-l-warning-600 bg-warning-50/70 dark:bg-warning-900/20">
                          <p className="text-sm text-warning-900 dark:text-warning-100 leading-relaxed">{rule.fallback_position}</p>
                        </Well>
                      </div>
                    )}

                    {/* Tags + Notes */}
                    {(rule.tags?.length > 0 || rule.notes) && (
                      <div className="pt-3 border-t border-hairline-soft space-y-3 max-w-2xl">
                        {rule.tags?.length > 0 && (
                          <div>
                            <SectionLabel icon={<PiTagLight />} label={t('Tags')} />
                            <div className="flex flex-wrap gap-1">
                              {rule.tags.map((tag, i) => (
                                <Badge key={i} color="purple">{tag}</Badge>
                              ))}
                            </div>
                          </div>
                        )}
                        {rule.notes && (
                          <div>
                            <SectionLabel icon={<PiNoteLight />} label={t('Notes')} />
                            <p className="text-sm text-fg-muted leading-relaxed">{rule.notes}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </GlassCard>
            );
          })}
        </div>
      )}

      </div>{/* end content area */}

      <FeatureTour
        steps={clauseRulesTourSteps}
        run={isTourRunning}
        onCallback={handleTourCallback}
        continuous
        showProgress
        showSkipButton
        disableOverlay
      />
    </div>
  );
};

export default ReviewClauseRules;
